import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Image as ImageIcon, X } from "lucide-react";
import type { MediaAsset } from "@/types/media";
import { MediaBrowserDialog } from "@/components/media/MediaBrowserDialog";

type MediaPickerButtonProps = {
  label?: string;
  multi?: boolean;
  initialParentId?: string | null;
  allowUpload?: boolean;
  onPick: (items: MediaAsset[]) => void;

  // UI
  size?: any;
  variant?: any;

  // Preview
  previewUrl?: string | null; // current image URL to preview
  previewAlt?: string;
  previewSize?: number; // px (square)
  onClear?: () => void; // optional clear action for the preview
};

export function MediaPickerButton({
  label = "Choose",
  multi = false,
  initialParentId = null,
  allowUpload = true,
  onPick,
  size = "default",
  previewUrl,
  previewAlt = "Selected image",
  previewSize = 36,
  onClear,
}: MediaPickerButtonProps) {
  const [open, setOpen] = useState(false);
  const [imgOk, setImgOk] = useState(true);

  const showPreview = !!previewUrl && imgOk;

  return (
    <div className="flex items-center gap-2">
      {/* Preview thumbnail */}
      {showPreview ? (
        <div
          className="rounded-md overflow-hidden border bg-slate-50 grid place-items-center"
          style={{ width: previewSize, height: previewSize }}
        >
          <img
            src={previewUrl as string}
            alt={previewAlt}
            className="w-full h-full object-cover transition-transform duration-200 hover:scale-110"
            onError={() => setImgOk(false)}
          />
        </div>
      ) : null}

      {/* Optional clear preview button */}
      {previewUrl && onClear && (
        <Button
          type="button"
          size="icon"
          variant="ghost"
          className="h-9 w-9"
          onClick={() => {
            setImgOk(true);
            onClear();
          }}
          title="Clear image"
        >
          <X className="h-4 w-4" />
        </Button>
      )}

      {/* Open picker */}
      <Button
        type="button"
        size={size}
        variant="outline"
        onClick={() => {
          setImgOk(true);
          setOpen(true);
        }}
        className="gap-2"
      >
        <ImageIcon className="h-4 w-4" />

        {/* smaller label */}
        <span className="text-xs text-slate-700 leading-none">
          {label}
        </span>
      </Button>

      <MediaBrowserDialog
        open={open}
        onOpenChange={setOpen}
        multi={multi}
        initialParentId={initialParentId}
        allowUpload={allowUpload}
        onPick={(items) => {
          onPick(items);
          setImgOk(true); // reset preview error state for the newly picked image
          setOpen(false);
        }}
        title="Pick media"
        description="Select item(s) to attach."
      />
    </div>
  );
}
